// @ts-nocheck
import React, { useState } from 'react';
import { useAuth } from '@/auth/clerkShim';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import usePaymentStore from '@/store/paymentStore';

// Extract checkout session objects from pasted JSON.
// Accepts:
//   - A single full event:  { "type": "checkout.session.completed", "data": { "object": {...} } }
//   - A single session obj: { "id": "cs_live_...", "metadata": {...}, ... }
//   - An array of either of the above
function parseInput(raw: string): { sessions: object[]; error: string | null } {
  let parsed: any;
  try {
    parsed = JSON.parse(raw.trim());
  } catch {
    return { sessions: [], error: 'Invalid JSON. Check for syntax errors.' };
  }

  const items: any[] = Array.isArray(parsed) ? parsed : [parsed];
  const sessions: object[] = [];

  for (const item of items) {
    if (item?.data?.object) {
      // Full event format — extract data.object
      sessions.push(item.data.object);
    } else if (item?.id) {
      // Bare session object
      sessions.push(item);
    } else {
      return { sessions: [], error: `Unrecognized object format (no "id" or "data.object"): ${JSON.stringify(item).slice(0, 80)}` };
    }
  }

  if (sessions.length === 0) {
    return { sessions: [], error: 'No session objects found in the pasted JSON.' };
  }

  return { sessions, error: null };
}

const ReplayWebhooks = () => {
  const { getToken } = useAuth();
  const replayStripeEvents = usePaymentStore((state) => state.replayStripeEvents);
  const [input, setInput] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleReplay = async () => {
    const { sessions, error } = parseInput(input);
    if (error) {
      setParseError(error);
      return;
    }
    setParseError(null);
    setLoading(true);
    setResults([]);
    try {
      const token = await getToken();
      const data = await replayStripeEvents(sessions, token);
      setResults(data || []);
    } catch (err) {
      setResults([{ id: 'request', success: false, error: err.message }]);
    } finally {
      setLoading(false);
    }
  };

  const successCount = results.filter((r) => r.success).length;
  const failCount = results.filter((r) => !r.success).length;

  return (
    <PageContainer className="max-w-3xl">
      <PageHeader
        title="Stripe Webhook Replay"
        description="Replay failed Stripe webhook events using the raw event JSON from the Stripe dashboard. Emails are suppressed during replay."
      />

      <Card className="shadow-sm border-0">
          <CardHeader>
            <CardTitle>Event JSON</CardTitle>
            <CardDescription>
              <span className="block mb-1">
                Go to <strong>Stripe Dashboard → Developers → Webhooks → (your endpoint) → Failed Events</strong>, click a failed event, and copy the full JSON payload.
              </span>
              <span className="block text-xs text-muted-foreground">
                Accepts: a single event object, a bare checkout session object, or a JSON array of either.
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={input}
              onChange={(e) => { setInput(e.target.value); setParseError(null); }}
              placeholder={'{\n  "type": "checkout.session.completed",\n  "data": {\n    "object": { "id": "cs_live_...", "metadata": { ... }, ... }\n  }\n}'}
              rows={12}
              className="font-mono text-xs"
            />
            {parseError && (
              <p className="text-sm text-destructive font-medium">{parseError}</p>
            )}
            <Button onClick={handleReplay} disabled={loading || !input.trim()} size="lg">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Replaying...' : 'Replay Events'}
            </Button>
          </CardContent>
        </Card>

        {results.length > 0 && (
          <Card className="shadow-sm border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                Results
                {successCount > 0 && (
                  <Badge className="bg-success/10 text-success">{successCount} succeeded</Badge>
                )}
                {failCount > 0 && (
                  <Badge className="bg-destructive/10 text-destructive">{failCount} failed</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {results.map((r, i) => (
                  <div
                    key={r.id + i}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      r.success ? 'bg-success/10 border-success/30' : 'bg-destructive/10 border-destructive/30'
                    }`}
                  >
                    {r.success ? (
                      <CheckCircle className="h-5 w-5 text-success mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-medium text-foreground">{r.id}</p>
                      {r.success ? (
                        <p className="text-sm text-success mt-0.5">
                          {r.isNew === true
                            ? 'Created new payment record'
                            : r.isNew === false
                            ? 'Updated existing payment record (safe to replay again)'
                            : 'Processed successfully'}
                        </p>
                      ) : (
                        <p className="text-sm text-destructive mt-0.5">{r.error}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
    </PageContainer>
  );
};

export default ReplayWebhooks;
